import { useRef, useState } from 'react'
import { FileDown, Image, ListChecks, Pencil, Plus } from 'lucide-react'
import type { ActionItem, CardLayout, Dashboard, DashboardCard } from '../types'
import { DashboardCardView } from './DashboardCard'
import { ExportLayout } from './ExportLayout'
import { NameDialog } from './NameDialog'
import { SnapGrid, type GridItem } from './SnapGrid'
import { exportPdf, exportPng } from '../util/exporter'
import { buildExportFilename, exportScope, REPORT_TYPES } from '../util/exportFilename'
import { todayIso } from '../format'
import { ACTIVE_SUPPLIER, TENANT } from '../tenant'
import { SKIPPED, useDownload } from '../useDownload'
import { DownloadError } from './DownloadError'
import { InfoTip } from './InfoTip'

const COLS = 12

function defaultLayout(card: DashboardCard, i: number): CardLayout {
  // Legacy cards without a stored layout: flow 3-across so they fill the width.
  const short = card.type === 'text' || card.type === 'action'
  return { x: (i % 3) * 4, y: Math.floor(i / 3) * 8, w: 4, h: short ? 3 : 8 }
}

export function DashboardView({
  dashboard,
  dashboards,
  refreshingIds,
  cardErrors,
  onLayoutChange,
  onRenameCard,
  onEditText,
  onRefreshCard,
  onRegroundCard,
  onRemoveCard,
  onMoveCard,
  onRenameBoard,
  onAddText,
  onAddAction,
  onEditAction,
  onApplyTemplate,
}: {
  dashboard: Dashboard
  dashboards: Dashboard[]
  refreshingIds: Set<string>
  cardErrors?: Record<string, string>
  onLayoutChange: (layouts: Record<string, CardLayout>) => void
  onRenameCard: (cardId: string, title: string) => void
  onEditText: (cardId: string, patch: { heading?: string; body?: string }) => void
  onRefreshCard: (cardId: string) => void
  // Absent when the chat is unavailable (MP-OS-193): no re-ask button and no chat hint.
  onRegroundCard?: (cardId: string) => void
  onRemoveCard: (cardId: string) => void
  onMoveCard: (cardId: string, targetId: string) => void
  onRenameBoard: (name: string) => void
  onAddText: () => void
  onAddAction: () => void
  onEditAction: (cardId: string, patch: { heading?: string; items?: ActionItem[] }) => void
  onApplyTemplate: (key: 'meeting' | 'monthly') => void
}) {
  const exportRef = useRef<HTMLDivElement | null>(null)
  const [renaming, setRenaming] = useState(false)

  const gridItems: GridItem[] = dashboard.cards.map((card, i) => ({
    id: card.id,
    layout: card.layout ?? defaultLayout(card, i),
    minW: 4,
    minH: card.type === 'text' || card.type === 'action' ? 2 : 6,
  }))
  const cardById = new Map(dashboard.cards.map((c) => [c.id, c]))

  // MP-OS-190: PNG and PDF share one guard (useDownload), so one click makes one file and the
  // other button waits; the file is named by buildExportFilename (supplier, board, export date).
  const download = useDownload(async (signal, kind: 'png' | 'pdf') => {
    if (!exportRef.current) return SKIPPED
    const filename = buildExportFilename({
      reportType: REPORT_TYPES.dashboard,
      scope: exportScope(TENANT.manufacturer, ACTIVE_SUPPLIER.label),
      title: dashboard.name,
      from: todayIso(),
      extension: kind,
    })
    try {
      if (kind === 'png') await exportPng(exportRef.current, filename, signal)
      else await exportPdf(exportRef.current, filename, signal)
    } catch (e) {
      // A failed capture used to fail SILENTLY — no file, no message, looks like success.
      console.error('export failed', e)
      throw e
    }
  })
  const label = (kind: 'png' | 'pdf', idle: string) => download.pending?.[0] !== kind ? idle
    : download.busy ? 'Exporting…' : download.done ? `${idle} ✓` : idle

  const empty = dashboard.cards.length === 0

  return (
    <div className="dash">
      <header className="dash-head">
        {/* MP-OS-189 — renamed through the same dialog that names a new board: the name only
            changes on Save/Enter, never keystroke by keystroke, and Escape keeps the old one. */}
        <div className="dash-title-row">
          <h2 className="dash-title" title={dashboard.name}>{dashboard.name}</h2>
          <button type="button" className="dash-rename" onClick={() => setRenaming(true)} aria-label={`Rename ${dashboard.name}`}>
            <Pencil size={14} aria-hidden /> Rename
          </button>
        </div>
        <div className="dash-actions">
          <button onClick={onAddText}>
            <Plus size={15} /> Text card
          </button>
          <button onClick={onAddAction}>
            <ListChecks size={15} /> Action card
          </button>
          <button onClick={() => download.start('png')} disabled={download.locked || empty}>
            <Image size={15} /> {label('png', 'PNG')}
          </button>
          <button onClick={() => download.start('pdf')} disabled={download.locked || empty}>
            <FileDown size={15} /> {label('pdf', 'PDF')}
          </button>
        </div>
        {download.error && (
          <DownloadError className="dash-export-error" message="Could not create the export — try again in a moment."
            onRetry={download.retry} />
        )}
      </header>

      {empty ? (
        <div className="dash-empty">
          <p>
            {/* MPD-64: what to do stays visible; how cards behave is in the ⓘ. */}
            {onRegroundCard ? <>
              Empty dashboard. Pin an answer from the chat, or add a <strong>Text card</strong>.
              <InfoTip label="About dashboard cards" size="small">
                A pinned answer becomes a card you can drag, resize and arrange. Text cards hold headings and
                comments.
              </InfoTip>
            </> : <>
              Empty dashboard. Add a <strong>Text card</strong> or start from a template.
              <InfoTip label="About dashboard cards" size="small">
                Cards can be dragged, resized and arranged. Text cards hold headings and comments.
              </InfoTip>
            </>}
          </p>
          <div className="dash-templates">
            <span className="dash-templates-label">Start from a template:</span>
            <button onClick={() => onApplyTemplate('meeting')}>Meeting notes</button>
            <button onClick={() => onApplyTemplate('monthly')}>Monthly review</button>
          </div>
        </div>
      ) : (
        <div className="grid-wrap">
          <SnapGrid
            items={gridItems}
            cols={COLS}
            rowHeight={44}
            gap={16}
            onChange={onLayoutChange}
            renderItem={(id) => {
              const card = cardById.get(id)
              if (!card) return null
              return (
                <DashboardCardView
                  card={card}
                  dashboards={dashboards}
                  currentId={dashboard.id}
                  refreshing={refreshingIds.has(card.id)}
                  error={cardErrors?.[card.id]}
                  onRename={(title) => onRenameCard(card.id, title)}
                  onEditText={(patch) => onEditText(card.id, patch)}
                  onEditAction={(patch) => onEditAction(card.id, patch)}
                  onRefresh={() => onRefreshCard(card.id)}
                  onReground={onRegroundCard && (() => onRegroundCard(card.id))}
                  onRemove={() => onRemoveCard(card.id)}
                  onMove={(targetId) => onMoveCard(card.id, targetId)}
                />
              )
            }}
          />
        </div>
      )}

      <ExportLayout ref={exportRef} dashboard={dashboard} />
      {renaming && (
        <NameDialog
          title="Rename dashboard"
          initialName={dashboard.name}
          confirmLabel="Save"
          onCancel={() => setRenaming(false)}
          onConfirm={(name) => {
            setRenaming(false)
            if (name !== dashboard.name) onRenameBoard(name)
          }}
        />
      )}
    </div>
  )
}
